import { useCallback, useEffect, useRef } from 'react';
import { toast, Slide } from 'react-toastify';
import { LoiForm } from '../../../../../slices/marketplace/loi-form/loi-form';
import {
    PatchLoiFormPayload,
    patchLoiForm,
} from '../../../../../slices/marketplace/loi-form/loi-forms-api';

export const AUTO_SAVE_CONTAINER_ID = 'auto-save-toast-container';

export type AutoSaveSection =
    | 'termForm'
    | `legalIssues:${string}`
    | 'additionalCovenants';

interface UseAutoSaveOptions {
    loiFormId: string | undefined;
    axios:
        | {
              patch: (
                  url: string,
                  data: PatchLoiFormPayload
              ) => Promise<{ status: number }>;
          }
        | undefined;
    formData: LoiForm | null;
    isEnabled: boolean;
    debounceMs?: number;
    showToast?: boolean;
}

interface UseAutoSaveReturn {
    markDirty: (sections: AutoSaveSection[]) => void;
    scheduleAutoSave: () => void;
    cancelPending: () => void;
}

export function useLoiFormAutoSave({
    loiFormId,
    axios,
    formData,
    isEnabled,
    debounceMs = 500,
    showToast = true,
}: UseAutoSaveOptions): UseAutoSaveReturn {
    const formDataRef = useRef<LoiForm | null>(formData);
    const dirtySectionsRef = useRef<Set<AutoSaveSection>>(new Set());
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isSavingRef = useRef(false);
    const executeSaveRef = useRef<() => Promise<void>>(async () => {});
    const toastIdRef = useRef<string>(
        `auto-save-${Math.random().toString(36).slice(2)}`
    );

    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    const executeSave = useCallback(async () => {
        if (!loiFormId || !axios || !formDataRef.current) return;

        const dirty = new Set(dirtySectionsRef.current);
        dirtySectionsRef.current.clear();

        if (dirty.size === 0) return;

        const current = formDataRef.current;
        const payload: PatchLoiFormPayload = {};
        if (dirty.has('termForm')) payload.termForm = current.termForm;
        if (dirty.has('additionalCovenants'))
            payload.additionalCovenants = current.additionalCovenants;

        const legalSectionIds = new Set<string>();
        Array.from(dirty).forEach((key) => {
            if (key.startsWith('legalIssues:')) {
                legalSectionIds.add(key.slice('legalIssues:'.length));
            }
        });
        if (legalSectionIds.size > 0) {
            payload.legalIssues = current.legalIssues.filter(
                (item) =>
                    item.sectionId != null &&
                    legalSectionIds.has(item.sectionId)
            );
            payload.legalIssueSectionIds = Array.from(legalSectionIds);
        }

        isSavingRef.current = true;

        if (showToast) {
            toast.loading('Saving...', {
                toastId: toastIdRef.current,
                containerId: AUTO_SAVE_CONTAINER_ID,
                position: 'bottom-right',
                transition: Slide,
                hideProgressBar: true,
                closeButton: false,
                className: 'auto-save-toast auto-save-toast--saving',
            });
        }

        try {
            await patchLoiForm(axios, loiFormId, payload);

            if (showToast) {
                toast.update(toastIdRef.current, {
                    containerId: AUTO_SAVE_CONTAINER_ID,
                    type: 'success',
                    render: '✓ Saved',
                    isLoading: false,
                    icon: false,
                    autoClose: 3000,
                    className: 'auto-save-toast auto-save-toast--success',
                });
            }
        } catch {
            if (showToast) {
                toast.update(toastIdRef.current, {
                    containerId: AUTO_SAVE_CONTAINER_ID,
                    type: 'error',
                    render: 'Auto-save failed. Use Save to retry',
                    isLoading: false,
                    autoClose: false,
                    className: 'auto-save-toast auto-save-toast--error',
                });
            }
        } finally {
            isSavingRef.current = false;
        }
    }, [loiFormId, axios, showToast]);

    useEffect(() => {
        executeSaveRef.current = executeSave;
    }, [executeSave]);

    const markDirty = useCallback(
        (sections: AutoSaveSection[]) => {
            if (!isEnabled || !loiFormId || !axios) return;
            sections.forEach((s) => dirtySectionsRef.current.add(s));
        },
        [isEnabled, loiFormId, axios]
    );

    const scheduleAutoSave = useCallback(() => {
        if (!isEnabled || !loiFormId || !axios) return;
        if (isSavingRef.current) return;
        if (dirtySectionsRef.current.size === 0) return;
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(executeSave, debounceMs);
    }, [isEnabled, loiFormId, axios, debounceMs, executeSave]);

    const cancelPending = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        dirtySectionsRef.current.clear();
    }, []);

    useEffect(() => {
        const toastId = toastIdRef.current;
        return () => {
            if (debounceTimerRef.current)
                clearTimeout(debounceTimerRef.current);
            if (dirtySectionsRef.current.size > 0) {
                executeSaveRef.current();
            }
            toast.dismiss(toastId);
        };
    }, []);

    return { markDirty, scheduleAutoSave, cancelPending };
}
